import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import axios from "axios";
import { useTheme, getColors } from "../ThemeContext";
import { useWell } from "../WellContext";
import { useAIAdvice } from "../useAIAdvice";
import ReactDOM from "react-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, LineChart, Line, Legend,
  ScatterChart, Scatter, Cell, ComposedChart, Area,
} from "recharts";

import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";

const C = {
  bg:      "#050b14",
  panel:   "#080f1e",
  card:    "#0b1525",
  border:  "#0f2040",
  border2: "#162a4a",
  cyan:    "#00d4ff",
  teal:    "#00ffcc",
  amber:   "#ffb84d",
  pink:    "#ff4da6",
  purple:  "#a855f7",
  blue:    "#3b9eff",
  red:     "#ff3d5a",
  green:   "#00ff88",
  orange:  "#ff7c2a",
  text:    "#e2eaf8",
  muted:   "#4a6080",
  muted2:  "#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

/* SHARED CHART CONSTANTS */

const CHART_MARGIN = { top: 8, right: 14, left: 52, bottom: 36 };

const LEGEND_PROPS = {
  verticalAlign: "top",
  align: "right",
  iconSize: 8,
  wrapperStyle: {
    fontFamily: MONO,
    fontSize: 10,
    paddingBottom: 6,
  },
};

// X-axis label
const X_LABEL_STYLE = (text) => ({
  value: text,
  position: "insideBottom",
  offset: -20,
  fill: C.muted,
  fontSize: 9,
  fontFamily: MONO,
});

const Y_LABEL_STYLE = (text) => ({
  value: text,
  angle: -90,
  position: "insideLeft",
  offset: 12,
  fill: C.muted,
  fontSize: 9,
  fontFamily: MONO,
});

/* TIMESTAMP HELPERS (same look as Overview / AI Controller: row 1 = cycle #, row 2 = cycle start time) */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD  = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };

// margin used by the three GLR charts once the timestamp row is shown (the "Cycle Index" label moves under it)
const CHART_MARGIN_TS = { top: 8, right: 14, left: 52, bottom: 20 };

function GLRDateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `cycle` and `_t` (ms). numeric = true for the scatter chart (number X axis). */
function glrDateXAxis(rows, numeric = false, nTicks = 5) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].map(i => stamped[i].cycle);
  const lookup = new Map(stamped.map(r => [r.cycle, r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey="cycle" type={numeric ? "number" : "category"}
      ticks={ticks} interval={0} tickLine={false} axisLine={false} height={32}
      tick={<GLRDateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={{ ...X_LABEL_STYLE("Cycle Index"), offset: -14 }} />
  );
}

/* DATA WINDOW (same bar as Overview / AI Controller) */
const HOUR = 3600000;
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
function fmtSpan(ms) {
  if (ms < HOUR)    return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}
const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

// same numbers the backend returns for stats / feasibility_summary, but computed on the cycles inside the window
function glrStatsOf(rows) {
  const v = rows.map(r => r.actual_glr).filter(x => typeof x === "number" && !isNaN(x));
  const m = rows.map(r => r.min_glr).filter(x => typeof x === "number" && !isNaN(x));
  const mg = rows.map(r => r.margin_pct).filter(x => typeof x === "number" && !isNaN(x));
  if (!v.length) return { stats: null, feasibility_summary: null };
  const n = v.length, mean = v.reduce((a, b) => a + b, 0) / n;
  const s = [...v].sort((a, b) => a - b);
  const median = n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
  const std = Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / n);
  const meanMin = m.length ? m.reduce((a, b) => a + b, 0) / m.length : 0;
  const feasible = rows.filter(r => r.feasible).length;
  return {
    stats: { mean, median, std, min: s[0], max: s[n - 1], count: n, mean_min_glr: meanMin },
    feasibility_summary: {
      feasible_count: feasible, infeasible_count: n - feasible, total_cycles: n,
      feasible_pct: (100 * feasible) / n, threshold: meanMin,
      mean_margin_pct: mg.length ? mg.reduce((a, b) => a + b, 0) / mg.length : 0,
    },
  };
}

function DataWindowBar({ win, setWin, stamped, view, presetCounts, tMin, tMax, isDark }) {
  const isAll   = win.mode === "all";
  const vStart  = view.length ? view[0]._t : null;
  const vEnd    = view.length ? view[view.length - 1]._t : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px", marginBottom: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {stamped.length} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window: too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range: widen the window to see a trend.
        </div>
      )}
    </div>
  );
}

/* GLR FIELD METADATA*/
const GLR_META = {
  actual_glr: {
    label: "Actual Gas-Liquid Ratio",
    unit: "scf/bbl",
    interpret: (v, row) => {
      if (v == null) return { color: C.muted, text: "No GLR data" };
      const minGlr = row?.min_glr;
      if (minGlr != null) {
        if (v > minGlr * 1.5) return { color: C.green, text: `WELL FEASIBLE: ${v.toFixed(0)} scf/bbl is ${((v / minGlr - 1) * 100).toFixed(0)}% above minimum.` };
        if (v > minGlr)       return { color: C.teal,  text: `FEASIBLE: ${v.toFixed(0)} scf/bbl marginally exceeds ${minGlr.toFixed(0)} scf/bbl minimum.` };
        return                       { color: C.red,   text: `BELOW THRESHOLD: ${v.toFixed(0)} scf/bbl is insufficient. Min required: ${minGlr.toFixed(0)} scf/bbl.` };
      }
      if (v > 2000) return { color: C.green, text: `HIGH GLR (${v.toFixed(0)} scf/bbl): strong gas drive for plunger lift.` };
      if (v > 500)  return { color: C.teal,  text: `MODERATE GLR (${v.toFixed(0)} scf/bbl): likely feasible, verify against well depth.` };
      return               { color: C.amber, text: `LOW GLR (${v.toFixed(0)} scf/bbl): may be insufficient for plunger lift.` };
    },
    advice: v => v > 2000
      ? "◆ High GLR indicates strong reservoir gas drive. Plunger lift is well-suited: focus on optimizing cycle timing."
      : v > 500
        ? "▲ Moderate GLR: plunger lift may work but monitor for liquid loading. Ensure shut-in builds adequate casing pressure."
        : "■ Low GLR: insufficient gas to reliably lift liquid slugs. Consider alternative lift methods or gas injection.",
  },
  min_glr: {
    label: "Minimum GLR Required",
    unit: "scf/bbl",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "-" };
      return { color: C.amber, text: `Threshold: ${v.toFixed(0)} scf/bbl: calculated from well depth & liquid rate.` };
    },
    advice: v => `The minimum GLR of ${v?.toFixed(0)} scf/bbl: (400/Rl)×(D/1000). If actual GLR is below this, the well cannot sustain plunger lift.`,
  },
  margin_pct: {
    label: "GLR Margin vs Minimum",
    unit: "%",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "-" };
      if (v > 50)  return { color: C.green, text: `+${v.toFixed(1)}% above minimum: healthy gas surplus.` };
      if (v > 0)   return { color: C.teal,  text: `+${v.toFixed(1)}% above minimum: marginal feasibility.` };
      if (v > -20) return { color: C.amber, text: `${v.toFixed(1)}% below minimum: borderline.` };
      return              { color: C.red,   text: `${v.toFixed(1)}% below minimum: not feasible.` };
    },
    advice: () => null,
  },
  liquid_bbl: {
    label: "Liquid Recovered per Cycle",
    unit: "barrels",
    status: (v, row) => {
      const avg = row?._avg_pos;
      if (v == null) return undefined;
      if (v <= 0) return "ZERO: no liquid recovered in this cycle (CRITICAL)";
      if (avg && v < 0.5 * avg) return `LOW: less than half of the average recovering cycle (${avg.toFixed(3)} barrels) (WARNING)`;
      return "GOOD: liquid recovered at or near the average recovering cycle";
    },
    interpret: (v, row) => {
      const avg = row?._avg_pos;
      if (v == null) return { color: C.muted, text: "No liquid data" };
      if (v <= 0)    return { color: C.red,   text: "CRITICAL: no liquid was recovered in this cycle." };
      if (avg && v < 0.5 * avg) return { color: C.amber, text: `WARNING: ${v.toFixed(3)} barrels is less than half of the average recovering cycle (${avg.toFixed(3)} barrels).` };
      return { color: C.green, text: `GOOD: ${v.toFixed(3)} barrels recovered, at or near the average recovering cycle.` };
    },
    advice: () => null,
  },
  cumulative_bbl: {
    label: "Total Recovered So Far",
    unit: "barrels",
    status: (v, row) => {
      if (v == null) return undefined;
      const last = row?._last10, exp = row?._exp10;
      if (last == null || exp == null) return undefined;
      if (last <= 0) return "ZERO: no liquid recovered in the last 10 cycles (CRITICAL)";
      if (last < 0.5 * exp) return `LOW: last 10 cycles added ${last.toFixed(3)} barrels, less than half of the usual ${exp.toFixed(3)} barrels (WARNING)`;
      return `GOOD: last 10 cycles added ${last.toFixed(3)} barrels, in line with the usual ${exp.toFixed(3)} barrels`;
    },
    interpret: (v, row) => {
      const last = row?._last10, exp = row?._exp10;
      if (v == null || last == null || exp == null) return null;
      if (last <= 0) return { color: C.red,   text: "CRITICAL: the total has not grown in the last 10 cycles (no liquid recovered)." };
      if (last < 0.5 * exp) return { color: C.amber, text: `WARNING: last 10 cycles added only ${last.toFixed(3)} barrels (usual: ${exp.toFixed(3)} barrels).` };
      return { color: C.green, text: `GOOD: last 10 cycles added ${last.toFixed(3)} barrels (usual: ${exp.toFixed(3)} barrels).` };
    },
    advice: () => null,
  },
  gas_val: {
    label: "Gas Rate (from CSV)",
    unit: "scf/day",
    interpret: () => null,
    advice: () => null,
  },
  liq_val: {
    label: "Liquid Rate (from CSV)",
    unit: "bbl/day",
    interpret: () => null,
    advice: () => null,
  },
};

/* SMART TOOLTIP */
function SmartTooltip({ active, payload: payloadIn, label, chartNote, focusField }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const payload  = payloadIn;
  const raw      = active && payload?.length ? (payload[0]?.payload || {}) : {};
  const cycleNum = raw.cycle ?? raw.index ?? null;

  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";

  if (active && payload?.length) {
    const primaryOrder = focusField
      ? [...payload].sort((a, b) => (b.dataKey === focusField) - (a.dataKey === focusField))
      : payload;
    for (const p of primaryOrder) {
      const key  = p.dataKey || p.name || "";
      const meta = GLR_META[key];
      if (meta && typeof p.value === "number" && primaryField === null) {
        primaryField = key;
        primaryValue = p.value;
        primaryUnit  = meta.unit || "";
      }
    }
  }

  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const rounded = Math.round(primaryValue * 10) / 10;

    const allFields = [];
    if (active && payload?.length) {
      for (const p of payload) {
        const key  = p.dataKey || p.name || "";
        const meta = GLR_META[key];
        if (typeof p.value === "number") {
          allFields.push({
            field: key,
            label: meta?.label || p.name || key,
            value: Math.round(p.value * 100) / 100,
            unit:  meta?.unit || "",
            ...(meta?.status ? { status: meta.status(p.value, raw) } : {}),
          });
        }
      }
    }

    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, primaryValue, primaryUnit, cycleNum, context, allFields);
  }, [active, primaryField, primaryValue, JSON.stringify(context)]);

  useEffect(() => {
    if (!active) clearAdvice();
  }, [active, clearAdvice]);

  if (!active || !payload?.length) return null;

  const TIP_W = 340;
  const TIP_H = 300;
  const GAP   = 14;
  const vw    = window.innerWidth;
  const vh    = window.innerHeight;
  let left    = mousePos.x + GAP;
  let top     = mousePos.y + GAP;
  if (left + TIP_W > vw - 8) left = mousePos.x - TIP_W - GAP;
  if (top  + TIP_H > vh - 8) top  = mousePos.y - TIP_H - GAP;

  const cycleLabel = cycleNum != null
    ? `Cycle ${cycleNum}`
    : label != null ? `Point: ${label}` : null;

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${C.border2}`,
      borderRadius: 12, padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 12px 40px #000e", maxWidth: TIP_W,
      zIndex: 99999, pointerEvents: "none",
    }}>
      {(cycleLabel || chartNote) && (
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
          {cycleLabel && (
            <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
              {cycleLabel}
            </div>
          )}
          {parseT(raw.time) != null && (
            <div style={{ color: C.amber, fontSize: 10, marginBottom: 2 }}>
              {fmtDT(parseT(raw.time))}
            </div>
          )}
          {chartNote && <div style={{ color: C.muted, fontSize: 9 }}>{chartNote}</div>}
        </div>
      )}

      {payload.map((p, i) => {
        const key         = p.dataKey || p.name || "";
        const meta        = GLR_META[key];
        const displayName = meta?.label || p.name || key;
        const unit        = meta?.unit  || "";
        const val         = p.value;
        const formatted   = typeof val === "number"
          ? (Math.abs(val) >= 100 ? val.toFixed(1) : val.toFixed(2))
          : String(val ?? "-");
        const interp = meta?.interpret ? meta.interpret(val, raw) : null;

        return (
          <div key={i} style={{ marginBottom: i < payload.length - 1 ? 10 : 0 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12,
              paddingBottom: interp ? 4 : 0,
            }}>
              <span style={{ color: C.muted, fontSize: 10, flex: 1, lineHeight: 1.3 }}>
                {displayName}
              </span>
              <span style={{ textAlign: "right", flexShrink: 0 }}>
                <span style={{ color: p.color || C.cyan, fontWeight: 700, fontSize: 12 }}>
                  {formatted}
                </span>
                {unit && (
                  <span style={{ color: C.muted, fontSize: 9, marginLeft: 3 }}>{unit}</span>
                )}
              </span>
            </div>
            {interp && (
              <div style={{
                marginTop: 3, padding: "4px 8px",
                background: `${interp.color}12`,
                border: `1px solid ${interp.color}33`,
                borderRadius: 6, fontSize: 10,
                color: interp.color, lineHeight: 1.5,
              }}>
                {interp.text}
              </div>
            )}
            {key === "actual_glr" && raw.status && (
              <div style={{
                marginTop: 3, padding: "3px 8px",
                background: raw.feasible ? `${C.green}10` : `${C.red}10`,
                border: `1px solid ${raw.feasible ? C.green : C.red}33`,
                borderRadius: 6, fontSize: 9,
                color: raw.feasible ? C.green : C.red,
              }}>
                {raw.feasible ? "FEASIBLE" : "BELOW THRESHOLD"}
                {raw.margin_pct != null && ` · Margin: ${raw.margin_pct > 0 ? "+" : ""}${raw.margin_pct.toFixed(1)}%`}
              </div>
            )}
            {i < payload.length - 1 && (
              <div style={{ height: 1, background: C.border, marginTop: 8 }} />
            )}
          </div>
        );
      })}

      {focusField && raw.liquid_height_ft != null && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
          <span style={{ color: C.muted, fontSize: 10 }}>Liquid Column Height</span>
          <span style={{ textAlign: "right" }}>
            <span style={{ color: C.teal, fontWeight: 700, fontSize: 12 }}>{raw.liquid_height_ft.toFixed(1)}</span>
            <span style={{ color: C.muted, fontSize: 9, marginLeft: 3 }}>feet</span>
          </span>
        </div>
      )}

      <div style={{
        marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color: C.muted }}>Analysing cycle data…</span>
          : advice
            ? renderAdvice(advice)
            : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>,
    document.body
  );
}

/* LIQUID RECOVERY - average / forecast settings */
const REC_WEAK_RATIO = 0.5;   // cycle is "weak" when below this share of the average recovering cycle

const fmtNum = v => (v == null || isNaN(v) ? "-" : Math.abs(v) >= 100 ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : Number(v).toFixed(Math.abs(v) >= 1 ? 2 : 3));

/* tooltip for the next-cycles forecast plot: values + AI recommendation on hover (same advice engine as the other GLR plots / Overview forecast) */
function ForecastTooltip({ active, payload, kpi, kpis }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [askedKey, setAskedKey] = useState(null);

  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const row = active && payload?.length ? (payload[0].payload || {}) : {};
  const unit = kpi?.unit || "";
  const isFc = !!(active && payload?.length && row.fc != null && row.x > (row.lastX ?? -Infinity));
  const isHist = !!(active && payload?.length && row.hist != null);
  const stepIdx = isFc && kpi ? (kpi.forecast || []).findIndex(f => f.x === row.x) : -1;
  const adviceKey = active && kpi ? (isFc ? `fc_${kpi.key}_${row.x}_${row.fc}` : isHist ? `${kpi.key}_${row.x}_${row.hist}` : null) : null;

  useEffect(() => {
    if (!adviceKey || !kpi) return;
    const timer = setTimeout(() => {
      const r2 = v => Math.round(v * 100) / 100;
      let fields, context, field, value;
      if (isFc && stepIdx >= 0) {
        const lastAct = kpi.history?.length ? kpi.history[kpi.history.length - 1].y : null;
        fields = [
          { field: "forecast_step", label: "Cycles ahead", value: stepIdx + 1, unit: "cycles" },
          ...(lastAct != null ? [{ field: "last_actual", label: `Last actual ${kpi.label}`, value: r2(lastAct), unit }] : []),
          ...(row.low != null && row.high != null ? [
            { field: "fc_low",  label: `${kpi.label} range low (~80%)`,  value: r2(row.low),  unit },
            { field: "fc_high", label: `${kpi.label} range high (~80%)`, value: r2(row.high), unit },
          ] : []),
          ...(kpis || []).map(k => {
            const pt = k.forecast?.[stepIdx];
            return pt?.y != null ? { field: k.key, label: `Forecast ${k.label}`, value: r2(pt.y), unit: k.unit } : null;
          }).filter(Boolean),
        ];
        field = `fc_${kpi.key}`; value = row.fc;
      } else {
        fields = [
          { field: kpi.key, label: kpi.label, value: r2(row.hist), unit },
          ...(row.avg != null ? [{ field: "avg_per_cycle", label: `Average per cycle (last cycles) ${kpi.label}`, value: r2(row.avg), unit }] : []),
        ];
        field = kpi.key; value = row.hist;
      }
      context = Object.fromEntries(fields.map(f => [f.field, f.value]));
      setAskedKey(adviceKey);
      fetchAdvice(field, value, unit, row.x, context, fields);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adviceKey]);

  useEffect(() => { if (!active) clearAdvice(); }, [active, clearAdvice]);

  if (!active || !payload?.length) return null;

  const TIP_W = 320, TIP_H = 300, GAP = 14;
  let left = mousePos.x + GAP, top = mousePos.y + GAP;
  if (left + TIP_W > window.innerWidth - 8)  left = mousePos.x - TIP_W - GAP;
  if (top + TIP_H > window.innerHeight - 8)  top  = mousePos.y - TIP_H - GAP;

  const ac = !advice ? C.muted
    : advice.startsWith("■") || advice.startsWith("▽") ? C.red
    : advice.startsWith("▲") || advice.startsWith("▶") ? C.amber
    : C.green;
  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", left, top, maxWidth: TIP_W, zIndex: 99999, pointerEvents: "none",
      background: C.card, border: `1px solid ${C.border2}`, borderRadius: 12, padding: "12px 16px",
      fontFamily: MONO, fontSize: 11, boxShadow: "0 12px 40px #000e",
    }}>
      <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
        <div style={{ color: isFc ? C.purple : C.cyan, fontWeight: 700, fontSize: 12 }}>
          Cycle {row.x}{isFc ? " · PREDICTED" : " · ACTUAL"}
        </div>
        {row._t != null && <div style={{ color: C.amber, fontSize: 10, marginTop: 2 }}>{fmtDT(row._t)}{isFc ? " (expected)" : ""}</div>}
        <div style={{ color: C.muted, fontSize: 9, marginTop: 2 }}>{kpi?.label}</div>
      </div>
      {row.hist != null && !isFc && <div style={{ color: C.cyan }}>Actual: {fmtNum(row.hist)} {unit}</div>}
      {row.avg != null && !isFc && <div style={{ color: C.green }}>Average per cycle: {fmtNum(row.avg)} {unit}</div>}
      {isFc && <div style={{ color: C.purple }}>Predicted: {fmtNum(row.fc)} {unit}</div>}
      {isFc && row.pred_avg != null && <div style={{ color: C.amber }}>Predicted average per cycle: {fmtNum(row.pred_avg)} {unit}</div>}
      {isFc && row.low != null && <div style={{ color: C.muted }}>~80% range: {fmtNum(row.low)} - {fmtNum(row.high)} {unit}</div>}
      <div style={{
        marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontSize: 10, lineHeight: 1.6,
        borderLeft: `3px solid ${ac}`, paddingLeft: 8,
      }}>
        {(askedKey !== adviceKey || loading) ? <span style={{ color: C.muted }}>Getting AI recommendation...</span>
          : advice ? renderAdvice(advice)
          : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>}
      </div>
    </div>,
    document.body
  );
}

function FadeCard({ children, delay = 0, style = {} }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "20px",
      animation: `fade-up 0.5s ease ${delay}s both`,
      ...style,
    }}>
      {children}
    </div>
  );
}
/* Chart body + field-engineer Summary toggle (same PlotSummary used on the other pages) */
function ChartWithSummary({ height, style, title, subtitle, accent, children }) {
  return (
    <>
      <div style={{ height, ...style }}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent}>{children}</PlotSummary>
    </>
  );
}
function Label({ children, color = C.muted }) {
  return (
    <div style={{
      fontFamily: MONO,
      fontSize: 9,
      color,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

function StatusBadge({ status, feasible }) {
  const color = feasible ? C.green : C.red;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      background: `${color}15`, border: `1px solid ${color}44`,
      borderRadius: 8, padding: "8px 16px", marginBottom: 14,
    }}>
      <span style={{ fontSize: 18, display:"flex", alignItems:"center" }}>{feasible ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00ff88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff3d5a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}</span>
      <span style={{
        fontFamily: MONO,
        fontSize: 12,
        color,
        fontWeight: 700,
        letterSpacing: 1,
      }}>
        {status}
      </span>
    </div>
  );
}

function Calculator({ wellSpecs }) {
  const defaultDepth = wellSpecs?.depth_ft || wellSpecs?.DepthFt || wellSpecs?.depth || 5000;
  const [depth,   setDepth]   = useState(String(defaultDepth));
  const [liqRate, setLiqRate] = useState("2");
  const [gasRate, setGasRate] = useState("2500");
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);

  async function calculate() {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/glr-feasibility/calculate`, {
        depth_ft: parseFloat(depth),
        liquid_rate_bbl_day: parseFloat(liqRate),
        gas_rate_scf_day: parseFloat(gasRate),
      });
      setResult(res.data);
    } catch {
      const D      = parseFloat(depth);
      const Rl     = parseFloat(liqRate);
      const Gr     = parseFloat(gasRate);
      const minGLR = (400 / Rl) * (D / 1000);
      const actGLR = Gr / Rl;
      const margin = ((actGLR - minGLR) / minGLR) * 100;
      setResult({
        min_glr_required: minGLR,
        actual_glr: actGLR,
        feasible: actGLR > minGLR,
        margin_pct: margin,
        status: actGLR > minGLR
          ? margin > 50 ? "WELL FEASIBLE: Healthy gas surplus" : "FEASIBLE: Marginally above threshold"
          : margin > -20 ? "BORDERLINE: Just below threshold" : "NOT FEASIBLE: Insufficient gas to lift liquid",
        advice: actGLR > minGLR
          ? `◆ GLR of ${actGLR.toFixed(0)} scf/bbl exceeds the ${minGLR.toFixed(0)} scf/bbl minimum for this ${D.toLocaleString()} ft well. Plunger lift is viable.`
          : `■ GLR of ${actGLR.toFixed(0)} scf/bbl is below the ${minGLR.toFixed(0)} scf/bbl minimum. Consider increasing shut-in time or evaluating alternative lift methods.`,
      });
    }
    setLoading(false);
  }

  const feasible    = result?.feasible;
  const marginColor = !result
    ? C.muted
    : feasible
      ? result.margin_pct > 50 ? C.green : C.teal
      : result.margin_pct > -20 ? C.amber : C.red;

  return (
    <FadeCard delay={0.1} style={{ borderTop: `3px solid ${C.cyan}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/><line x1="8" y1="18" x2="12" y2="18"/></svg>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
          Feasibility Calculator
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
        {[
          { label: "Well Depth (ft)",       key: "depth", val: depth,   set: setDepth,   ph: "5000" },
          { label: "Liquid Rate (bbl/day)", key: "liq",   val: liqRate, set: setLiqRate, ph: "2" },
          { label: "Gas Rate (scf/day)",    key: "gas",   val: gasRate, set: setGasRate, ph: "2500" },
        ].map(({ label, key, val, set, ph }) => (
          <div key={key}>
            <Label>{label}</Label>
            <input
              type="number"
              value={val}
              onChange={e => set(e.target.value)}
              placeholder={ph}
              style={{
                width: "100%",
                background: C.card,
                border: `1px solid ${C.border2}`,
                borderRadius: 8,
                color: C.text,
                fontFamily: MONO,
                fontSize: 13,
                padding: "10px 14px",
                outline: "none",
                transition: "border-color 0.2s, background 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={e => { e.target.style.borderColor = `${C.cyan}66`; }}
              onBlur={e  => { e.target.style.borderColor = C.border2; }}
            />
          </div>
        ))}
      </div>

      <button
        onClick={calculate}
        disabled={loading}
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: 8,
          border: "none",
          background: loading ? C.muted2 : `linear-gradient(135deg, ${C.cyan}, #0088bb)`,
          color: loading ? C.muted : "#000",
          fontFamily: SANS,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 2,
          textTransform: "uppercase",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "all 0.2s",
          marginBottom: result ? 18 : 0,
        }}
      >
        {loading ? "CALCULATING..." : "▶  RUN FEASIBILITY CHECK"}
      </button>

      {result && (
        <div style={{ animation: "fade-up 0.3s ease both" }}>
          <StatusBadge status={result.status} feasible={result.feasible} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            {[
              { label: "Minimum GLR Required", val: result.min_glr_required?.toFixed(1), unit: "scf/bbl", color: C.amber },
              { label: "Your Actual GLR",       val: result.actual_glr?.toFixed(1),        unit: "scf/bbl", color: feasible ? C.green : C.red },
              { label: "Margin",                val: (result.margin_pct >= 0 ? "+" : "") + result.margin_pct?.toFixed(1), unit: "%", color: marginColor },
            ].map(({ label, val, unit, color }) => (
              <div key={label} style={{
                background: `${color}08`,
                border: `1px solid ${color}22`,
                borderRadius: 10,
                padding: "14px 16px",
                textAlign: "center",
              }}>
                <Label color={C.muted}>{label}</Label>
                <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color }}>{val}</div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted2, marginTop: 3 }}>{unit}</div>
              </div>
            ))}
          </div>

          <div style={{
            background: `${feasible ? C.green : C.red}0a`,
            border: `1px solid ${feasible ? C.green : C.red}22`,
            borderRadius: 8,
            padding: "12px 16px",
            fontFamily: MONO,
            fontSize: 11,
            color: C.text,
            lineHeight: 1.7,
          }}>
            {renderAdvice(result.advice)}
          </div>
        </div>
      )}
    </FadeCard>
  );
}

function DatasetGLRPanel({ glrData, loading }) {
  const [view, setView] = useState("scatter");

  if (loading) {
    return (
      <FadeCard delay={0.2}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, textAlign: "center", padding: "40px 0" }}>
          Loading GLR data from your dataset...
        </div>
      </FadeCard>
    );
  }
  if (!glrData) return null;

  const perCycle = glrData.per_cycle_data || glrData.trend_data || [];
  const { stats, feasibility_summary, columns_used } = glrData;
  const hasT     = perCycle.some(d => d._t != null);
  const tsMargin = hasT ? CHART_MARGIN_TS : CHART_MARGIN;

  if (!perCycle.length) {
    return (
      <FadeCard delay={0.2} style={{ borderTop: `3px solid ${C.red}` }}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.red, padding: "20px 0", textAlign: "center" }}>
          Could not compute GLR from CSV.{" "}
          {glrData.error && <span style={{ color: C.muted }}>{glrData.error}</span>}
          <div style={{ marginTop: 10, color: C.muted, fontSize: 9 }}>
            Columns in CSV: {glrData.columns_in_csv?.join(", ")}
          </div>
        </div>
      </FadeCard>
    );
  }

  const feasibleCount   = perCycle.filter(d => d.feasible).length;
  const infeasibleCount = perCycle.length - feasibleCount;
  const avgGLR          = stats?.mean ?? 0;
  const threshold       = feasibility_summary?.threshold ?? 0;

  return (
    <FadeCard delay={0.2} style={{ borderTop: `3px solid ${C.teal}` }}>

      {/* Header*/}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
            GLR: Per-Cycle Values
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{
            background: `${C.green}15`, border: `1px solid ${C.green}33`,
            borderRadius: 8, padding: "6px 14px", textAlign: "center",
          }}>
            <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: C.green }}>
              {feasibility_summary?.feasible_pct?.toFixed(1)}%
            </div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
              FEASIBLE ({feasibleCount})
            </div>
          </div>
          <div style={{
            background: `${C.red}15`, border: `1px solid ${C.red}33`,
            borderRadius: 8, padding: "6px 14px", textAlign: "center",
          }}>
            <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: C.red }}>
              {(100 - (feasibility_summary?.feasible_pct ?? 0)).toFixed(1)}%
            </div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
              BELOW THRESHOLD ({infeasibleCount})
            </div>
          </div>
        </div>
      </div>

      {/* Stats row*/}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 18 }}>
          {[
            { label: "Mean GLR",    val: stats.mean,        color: C.cyan   },
            { label: "Median",      val: stats.median,      color: C.teal   },
            { label: "Std",     val: stats.std,         color: C.purple },
            { label: "Min",         val: stats.min,         color: C.green  },
            { label: "Max",         val: stats.max,         color: C.amber  },
            { label: "Min Required",val: stats.mean_min_glr,color: C.red    },
          ].map(({ label, val, color }) => (
            <div key={label} style={{
              background: C.panel, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: "10px 12px", textAlign: "center",
            }}>
              <Label color={C.muted}>{label}</Label>
              <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color }}>
                {val?.toFixed(1)}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted2 }}>scf/bbl</div>
            </div>
          ))}
        </div>
      )}

      {/* View Toggle */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {[
          { id: "scatter", label: "Actual GLR per Cycle",  color: C.cyan  },
          { id: "margin",  label: "Margin % per Cycle",    color: C.teal  },
        ].map(btn => {
          const active = view === btn.id;
          return (
            <button
              key={btn.id}
              onClick={() => setView(btn.id)}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: `1.5px solid ${active ? btn.color : C.border2}`,
                background: active
                  ? `linear-gradient(135deg, ${btn.color}22, ${btn.color}0a)`
                  : `${C.panel}`,
                color: active ? btn.color : C.muted,
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: active ? 700 : 400,
                cursor: "pointer",
                letterSpacing: 0.8,
                boxShadow: active ? `0 0 12px ${btn.color}33` : "none",
                transition: "all 0.18s ease",
              }}
            >
              {btn.label}
            </button>
          );
        })}
      </div>

      {/* Actual GLR per Cycle*/}
      {view === "scatter" && (
        <ChartWithSummary height={280} accent={C.cyan} title="Actual GLR per Cycle" subtitle="Actual gas-liquid ratio of each cycle against the minimum GLR required; feasible versus below-threshold cycles">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={tsMargin}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="cycle"
                name="Cycle"
                type="number"
                tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                tickLine={false}
                tickFormatter={v => `C${v}`}
                interval={Math.max(0, Math.floor(perCycle.length / 12) - 1)}
                height={hasT ? 20 : undefined}
                label={hasT ? undefined : X_LABEL_STYLE("Cycle Index")}
              />
              {glrDateXAxis(perCycle, true)}
              <YAxis
                dataKey="actual_glr"
                name="Actual GLR"
                type="number"
                width={72}
                tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                tickLine={false}
                axisLine={false}
                label={Y_LABEL_STYLE("GLR (scf/bbl)")}
              />
              <Tooltip
                content={
                  <SmartTooltip chartNote="Each dot = one real cycle. Green = feasible (actual GLR > min required). Red = below threshold." />
                }
              />
              <ReferenceLine
                y={threshold}
                stroke={C.amber}
                strokeDasharray="5 3"
                label={{
                  value: `MIN REQUIRED (avg ${threshold.toFixed(0)} scf/bbl)`,
                  fill: C.amber, fontSize: 8, fontFamily: MONO, position: "right",
                }}
              />
              <ReferenceLine
                y={avgGLR}
                stroke={C.cyan}
                strokeDasharray="3 3"
                strokeOpacity={0.5}
                label={{
                  value: `MEAN ${avgGLR.toFixed(0)}`,
                  fill: C.cyan, fontSize: 8, fontFamily: MONO, position: "left",
                }}
              />
              {/* invisible twin bound to the timestamp axis so it gets the exact same scale as the cycle axis */}
              {hasT && <Scatter data={perCycle} xAxisId="date" tooltipType="none" legendType="none" isAnimationActive={false} shape={() => null} />}
              <Scatter data={perCycle} name="actual_glr">
                {perCycle.map((entry, idx) => (
                  <Cell key={idx} fill={entry.feasible ? C.teal : C.red} fillOpacity={0.75} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </ChartWithSummary>
      )}

      {/* Margin % per Cycle */}
      {view === "margin" && (
        <ChartWithSummary height={280} accent={C.teal} title="Margin % per Cycle" subtitle="GLR margin above or below the minimum GLR threshold for each cycle">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perCycle} margin={tsMargin}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="cycle"
                tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                tickLine={false}
                tickFormatter={v => `C${v}`}
                interval={Math.max(0, Math.floor(perCycle.length / 12) - 1)}
                height={hasT ? 20 : undefined}
                label={hasT ? undefined : X_LABEL_STYLE("Cycle Index")}
              />
              {glrDateXAxis(perCycle)}
              <YAxis
                width={72}
                tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => `${v > 0 ? "+" : ""}${v.toFixed(0)}%`}
                label={Y_LABEL_STYLE("GLR Margin %")}
              />
              <Tooltip
                content={
                  <SmartTooltip chartNote="Bar height = how far above/below the minimum GLR threshold this cycle was. Green = feasible, red = not feasible." />
                }
              />
              <ReferenceLine y={0} stroke={C.amber} strokeWidth={1.5} />
              <Bar dataKey="margin_pct" name="margin_pct" radius={[2, 2, 0, 0]}>
                {perCycle.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={
                      entry.margin_pct > 50  ? C.green :
                      entry.margin_pct > 0   ? C.teal  :
                      entry.margin_pct > -20 ? C.amber : C.red
                    }
                    fillOpacity={0.8}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWithSummary>
      )}

      {/* Legend: shown only for scatter view */}
      {view === "scatter" && (
        <div style={{
          marginTop: 14, padding: "10px 14px",
          background: `${C.panel}cc`, border: `1px solid ${C.border}`,
          borderRadius: 8, display: "flex", flexWrap: "wrap", gap: 16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.teal }} />
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.teal }}>Feasible cycle (actual GLR &gt; min required)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.red }} />
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.red }}>Below threshold (actual GLR &lt; min required)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 20, height: 2, background: C.amber }} />
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.amber }}>Avg min GLR required</span>
          </div>
        </div>
      )}
    </FadeCard>
  );
}

/* GLR TREND OVER CYCLES */
function GLRTrendPanel({ trendData, loading }) {
  if (loading || !trendData?.length) return null;

  const feasibleCount = trendData.filter(d => d.feasible).length;
  const feasiblePct   = ((feasibleCount / trendData.length) * 100).toFixed(1);
  const hasT          = trendData.some(d => d._t != null);

  return (
    <FadeCard delay={0.3} style={{ borderTop: `3px solid ${C.amber}` }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
          <div>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
              GLR Trend Across Cycles
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>
              Actual GLR vs Minimum Required: each point is a real cycle value
            </div>
          </div>
        </div>
        <div style={{
          background: `${C.green}15`, border: `1px solid ${C.green}33`,
          borderRadius: 8, padding: "6px 14px", textAlign: "center",
        }}>
          <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: C.green }}>
            {feasiblePct}%
          </div>
          <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>CYCLES FEASIBLE</div>
        </div>
      </div>
      <ChartWithSummary height={300} accent={C.amber} title="GLR Trend Across Cycles" subtitle="Actual GLR versus minimum required GLR across cycles; trend of gas lift feasibility">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData} margin={hasT ? CHART_MARGIN_TS : CHART_MARGIN}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="cycle"
              tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
              tickLine={false}
              tickFormatter={v => `C${v}`}
              interval={Math.max(0, Math.floor(trendData.length / 10) - 1)}
              height={hasT ? 20 : undefined}
              label={hasT ? undefined : X_LABEL_STYLE("Cycle Index")}
            />
            {glrDateXAxis(trendData)}
            <YAxis
              width={72}
              tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
              tickLine={false}
              axisLine={false}
              label={Y_LABEL_STYLE("GLR (scf/bbl)")}
            />
            <Tooltip
              content={
                <SmartTooltip chartNote="Actual GLR vs minimum threshold per cycle: each point = real measured value from your CSV." />
              }
            />
            <Legend
              {...LEGEND_PROPS}
              formatter={key => GLR_META[key]?.label || key}
            />
            <Line
              type="monotone"
              dataKey="actual_glr"
              name="actual_glr"
              stroke={C.cyan}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, fill: C.cyan, stroke: "#000", strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="min_glr"
              name="min_glr"
              stroke={C.amber}
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 4, fill: C.amber, stroke: "#000", strokeWidth: 1 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartWithSummary>

      <div style={{
        marginTop: 14, padding: "10px 14px",
        background: `${C.panel}cc`, border: `1px solid ${C.border}`,
        borderRadius: 8, display: "flex", flexWrap: "wrap", gap: 20,
      }}>
        {[
          { color: C.cyan,  dash: false, label: "Actual GLR per Cycle",    desc: "Real value from your CSV (scf/bbl)" },
          { color: C.amber, dash: true,  label: "Min Required GLR", desc: "Computed per cycle from well depth & liquid rate" },
        ].map(item => (
          <div key={item.label} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{
              width: 22, height: 2, marginTop: 7, flexShrink: 0,
              background: item.dash
                ? `repeating-linear-gradient(90deg, ${item.color} 0, ${item.color} 5px, transparent 5px, transparent 8px)`
                : item.color,
            }} />
            <div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: item.color, fontWeight: 700 }}>
                {item.label}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 1 }}>
                {item.desc}
              </div>
            </div>
          </div>
        ))}
      </div>
    </FadeCard>
  );
}

/* REFERENCE TABLE */
function DepthTable() {
  const { apiUrl, selectedWell } = useWell();
  const [config, setConfig] = useState(null);
  const [refView, setRefView] = useState("recovery");   // "recovery" = liquid recovery by cycle (plot + table), "table" = minimum GLR reference table

  useEffect(() => {
    axios.get(apiUrl("/depth-table-config"))
      .then(res => setConfig(res.data))
      .catch(() => setConfig({ depths: [], rates: [] }));
  }, [selectedWell]);   // eslint-disable-line react-hooks/exhaustive-deps

  const recoveryRows = useMemo(() => {
    const raw = config?.liquid_recovery_by_cycle || [];
    const pos = raw.filter(r => r.liquid_bbl > 0);
    const avgPos = pos.length ? pos.reduce((a, r) => a + r.liquid_bbl, 0) / pos.length : 0;
    const meanAll = raw.length ? raw.reduce((a, r) => a + r.liquid_bbl, 0) / raw.length : 0;
    return raw.map((r, i) => {
      let last10 = 0;
      for (let k = Math.max(0, i - 9); k <= i; k++) last10 += raw[k].liquid_bbl;
      return { ...r, _t: parseT(r.time), _avg_pos: avgPos, _last10: last10, _exp10: meanAll * 10 };
    });
  }, [config]);
  const [openRows, setOpenRows] = useState({});   // cycle -> expanded in the recovery table
  const [recFocus, setRecFocus] = useState("liquid_bbl");   // which series the mouse is on (bar or line)
  const recoveryHasT = recoveryRows.some(r => r._t != null);
  const recoverySummary = config?.liquid_recovery_summary || null;
  const recoveryForecast = config?.liquid_recovery_forecast || null;
  const [fcKey, setFcKey] = useState("liquid_bbl");   // which KPI the next-cycles forecast plot shows
  const fcKpis = recoveryForecast?.kpis || [];
  const fcKpi  = fcKpis.find(k => k.key === fcKey) || fcKpis[0] || null;
  const fcTimes = recoveryForecast?.x_times || [];
  const fcChart = useMemo(() => {
    if (!fcKpi) return { data: [], lastX: 0 };
    const hist = fcKpi.history || [];
    const lastX = hist.length ? hist[hist.length - 1].x : 0;
    const rows = hist.map(h => ({ x: h.x, hist: h.y, avg: h.avg, lastX }));   // avg = average per cycle of the actual cycles (per-cycle curve)
    if (hist.length && fcKpi.forecast.length) {   // joins the dashed lines to the last actual point
      rows[rows.length - 1].fc = hist[hist.length - 1].y;
      rows[rows.length - 1].pred_avg = hist[hist.length - 1].avg;
    }
    fcKpi.forecast.forEach((f, i) => rows.push({
      x: f.x, fc: f.y, low: f.low, high: f.high, _t: parseT(fcTimes[i]), lastX, pred_avg: f.avg,   // pred_avg = average per cycle of the predicted cycles
      bandBase: f.low, bandSpread: Math.max(f.high - f.low, 0),
    }));
    return { data: rows, lastX };
  }, [fcKpi, fcTimes]);
  const recHeaders = [
    "CYCLE", "TIMESTAMP", "LIQUID RECOVERED (barrels)", "TOTAL RECOVERED SO FAR (barrels)", "LIQUID COLUMN HEIGHT (feet)",
    "AVERAGE PER CYCLE SO FAR (barrels)",
    `AVERAGE PER CYCLE, LAST ${recoverySummary?.rolling_window ?? ""} CYCLES (barrels)`,
    "VS OVERALL AVERAGE (%)", "SHUT-IN TIME (min)", "ACTUAL GLR (scf/bbl)", "RECOVERY STATUS",
  ];

  if (!config) {
    return (
      <FadeCard delay={0.4}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, textAlign: "center", padding: "40px 0" }}>
          Loading depth table from well data...
        </div>
      </FadeCard>
    );
  }

  if (!config.depths.length || !config.rates.length) {
    return (
      <FadeCard delay={0.4}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.red, textAlign: "center", padding: "40px 0" }}>
          Could not load depth table: check /api/depth-table-config
        </div>
      </FadeCard>
    );
  }

  const depths = config.depths;
  const rates  = config.rates;

  return (
    <FadeCard delay={0.4} style={{ borderTop: `3px solid ${C.blue}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        <div>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
            Liquid recovery by cycle & Minimum GLR Reference Table
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>
            Required scf/bbl by depth & liquid rate
            {config.actual_well_depth && (
              <span style={{ color: C.cyan }}> · Well depth: {config.actual_well_depth.toLocaleString()} ft</span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[
          { id: "recovery", label: "LIQUID RECOVERY BY CYCLE" },
          { id: "table",    label: "MINIMUM GLR TABLE" },
        ].map(t => {
          const active = refView === t.id;
          return (
            <button key={t.id} onClick={() => setRefView(t.id)} style={{
              fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
              background: active ? `${C.blue}22` : "transparent", color: active ? C.blue : C.muted,
              border: `1px solid ${active ? C.blue : C.border2}`, whiteSpace: "nowrap",
            }}>{t.label}</button>
          );
        })}
      </div>
      {refView === "table" && (<>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{
                padding: "8px 14px", textAlign: "left",
                color: C.muted, fontSize: 9, letterSpacing: 1,
                borderBottom: `1px solid ${C.border}`, background: C.panel,
              }}>
                DEPTH ↓ / LIQUID RATE →
              </th>
              {rates.map(r => (
                <th key={r} style={{
                  padding: "8px 14px", textAlign: "center",
                  color: C.teal, fontSize: 9, letterSpacing: 1,
                  borderBottom: `1px solid ${C.border}`, background: C.panel,
                }}>
                  {r} bbl/d
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {depths.map((d, di) => {
              const isActualDepth = config.actual_well_depth &&
                Math.abs(d - config.actual_well_depth) < 500;
              return (
                <tr key={d} style={{
                  background: di % 2 === 0 ? C.card : C.panel,
                  outline: isActualDepth ? `1px solid ${C.cyan}44` : "none",
                }}>
                  <td style={{
                    padding: "8px 14px", fontWeight: 700,
                    borderBottom: `1px solid ${C.border}`,
                    fontFamily: MONO, fontSize: 11,
                    color: isActualDepth ? C.cyan : C.amber,
                  }}>
                    {d.toLocaleString()} ft
                  </td>
                  {rates.map(r => {
                    const minGLR = (400 / r) * (d / 1000);
                    return (
                      <td key={r} style={{
                        padding: "8px 14px", textAlign: "center",
                        fontFamily: MONO, fontSize: 11,
                        color: minGLR > 5000 ? C.red : minGLR > 2000 ? C.amber : C.green,
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        {minGLR.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontFamily: MONO, fontSize: 9, color: C.muted }}>
        <span style={{ color: C.green }}>■</span> &lt;2,000 scf/bbl: low requirement &nbsp;
        <span style={{ color: C.amber }}>■</span> 2,000-5,000 scf/bbl: moderate &nbsp;
        <span style={{ color: C.red }}>■</span> &gt;5,000 scf/bbl: very high requirement
      </div>
      </>)}

      {refView === "recovery" && (
        recoveryRows.length === 0 ? (
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, textAlign: "center", padding: "40px 0" }}>
            No liquid volume per cycle available for this well.
          </div>
        ) : (
          <>
            {recoverySummary && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                {[
                  { label: "TOTAL RECOVERED",   val: `${recoverySummary.total_bbl.toFixed(2)} barrels`,              color: C.cyan },
                  { label: "AVG PER CYCLE",     val: `${recoverySummary.mean_bbl_per_cycle.toFixed(3)} barrels`,     color: C.teal },
                  { label: "BEST CYCLE",        val: `#${recoverySummary.best_cycle} · ${recoverySummary.max_bbl_per_cycle.toFixed(3)} barrels`, color: C.amber },
                  { label: "CYCLES RECOVERING", val: `${recoverySummary.cycles_with_recovery} of ${recoverySummary.total_cycles}`, color: C.green },
                  ...(recoverySummary.rolling_avg_bbl != null ? [{ label: `AVERAGE PER CYCLE (LAST ${recoverySummary.rolling_window})`, val: `${recoverySummary.rolling_avg_bbl.toFixed(3)} barrels`, color: C.blue }] : []),
                  ...(recoveryForecast ? [{ label: `PREDICTED NEXT ${recoveryForecast.horizon} CYCLES`, val: `${fmtNum(recoveryForecast.summary.predicted_total_bbl)} barrels`, color: C.orange }] : []),
                ].map(k => (
                  <div key={k.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderLeft: `3px solid ${k.color}`, borderRadius: 8, padding: "8px 14px", minWidth: 140 }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: C.muted }}>{k.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: k.color, marginTop: 3 }}>{k.val}</div>
                  </div>
                ))}
              </div>
            )}
            <ChartWithSummary accent={C.cyan} title="Liquid Recovery per Cycle" subtitle="Liquid recovered in each cycle and total recovered so far, with the average per cycle">
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={recoveryRows} margin={recoveryHasT ? { top: 8, right: 16, left: 8, bottom: 20 } : { top: 8, right: 16, left: 8, bottom: 36 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="cycle" tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false}
                  tickFormatter={v => `C${v}`}
                  interval={Math.max(0, Math.floor(recoveryRows.length / 12) - 1)}
                  height={recoveryHasT ? 20 : undefined}
                  label={recoveryHasT ? undefined : X_LABEL_STYLE("Cycle Index")} />
                {glrDateXAxis(recoveryRows)}
                <YAxis yAxisId="left" width={84} tickLine={false} axisLine={false}
                  tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  tickFormatter={v => Number(v).toFixed(2)}
                  label={{ value: "Liquid Recovered per Cycle (barrels)", angle: -90, position: "insideLeft", offset: 0, style: { textAnchor: "middle" }, fill: C.muted, fontSize: 10, fontFamily: MONO }} />
                <YAxis yAxisId="right" orientation="right" width={84} tickLine={false} axisLine={false}
                  tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  tickFormatter={v => Number(v).toFixed(1)}
                  label={{ value: "Total Recovered So Far (barrels)", angle: 90, position: "insideRight", offset: 0, style: { textAnchor: "middle" }, fill: C.muted, fontSize: 10, fontFamily: MONO }} />
                <Tooltip
                  content={
                    <SmartTooltip focusField={recFocus} chartNote="Bars = liquid recovered in each cycle. Line = total liquid recovered up to that cycle." />
                  }
                />
                <Legend {...LEGEND_PROPS} />
                {recoverySummary && (
                  <ReferenceLine yAxisId="left" y={recoverySummary.mean_bbl_per_cycle} stroke={C.green} strokeDasharray="5 3" strokeOpacity={0.7}
                    label={{ value: `AVERAGE ${recoverySummary.mean_bbl_per_cycle.toFixed(3)}`, fill: C.green, fontSize: 8, fontFamily: MONO, position: "insideTopLeft" }} />
                )}
                <Bar yAxisId="left" dataKey="liquid_bbl" name="Liquid Recovered per Cycle" fill={C.cyan} radius={[2, 2, 0, 0]} />
                <Line yAxisId="left" type="monotone" dataKey="rolling_avg_bbl" name="Average per Cycle" stroke={C.amber} strokeWidth={1.8} dot={false}
                  activeDot={{ r: 4, fill: C.amber, stroke: "#000", strokeWidth: 1 }} />
                <Line yAxisId="right" type="monotone" dataKey="cumulative_bbl" name="Total Recovered So Far" stroke={C.purple} strokeWidth={2} dot={false}
                  activeDot={{ r: 5, fill: C.purple, stroke: "#000", strokeWidth: 1,
                    onMouseEnter: () => setRecFocus("cumulative_bbl"), onMouseLeave: () => setRecFocus("liquid_bbl") }} />
              </ComposedChart>
            </ResponsiveContainer>
            </ChartWithSummary>

            {/* Separate plot: next-cycles forecast (same layout as the Overview forecast panel, GLR based) */}
            {fcKpi && (() => {
              const ac = C.purple;
              const sm = recoveryForecast.summary || {};
              const tiles = [
                { label: "PREDICTED LIQUID (TOTAL)", val: fmtNum(sm.predicted_total_bbl), unit: "barrels" },
                { label: "PREDICTED LIQUID PER CYCLE", val: fmtNum(sm.predicted_avg_bbl), unit: "barrels" },
                { label: "PREDICTED ACTUAL GLR", val: fmtNum(sm.predicted_glr), unit: "scf/bbl" },
                { label: "PREDICTED MINIMUM GLR", val: fmtNum(sm.predicted_min_glr), unit: "scf/bbl" },
                { label: "PREDICTED GLR MARGIN", val: sm.predicted_margin_pct != null ? `${sm.predicted_margin_pct > 0 ? "+" : ""}${fmtNum(sm.predicted_margin_pct)}` : "-", unit: "%" },
              ];
              const groups = [...new Set(fcKpis.map(k => k.group))];
              const trendArrow = k => { const h = k.history || []; const f = k.forecast?.[0]?.y; if (!h.length || f == null) return "→"; const d = f - h[h.length - 1].y; return d > 0 ? "↑" : d < 0 ? "↓" : "→"; };
              return (
                <div style={{ position: "relative", marginTop: 22, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 16px" }}>
                  <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 2, background: `linear-gradient(to right, transparent, ${ac}88, transparent)` }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 14, color: C.text }}>GLR Forecast: Next {recoveryForecast.horizon} Cycles</div>
                    <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: ac, background: `${ac}18`, border: `1px solid ${ac}55`, borderRadius: 3, padding: "2px 7px" }}>PREDICTION</span>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, margin: "3px 0 14px" }}>
                    {recoveryForecast.method}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
                    {tiles.map(t => (
                      <div key={t.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{t.label}</div>
                        <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: ac, marginTop: 4 }}>
                          {t.val}<span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>{t.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {groups.map(g => (
                    <div key={g} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted2 || C.muted, textTransform: "uppercase", letterSpacing: 1, minWidth: 78 }}>{g}</span>
                      {fcKpis.filter(k => k.group === g).map(k => {
                        const sel = k.key === fcKpi.key;
                        const arrow = trendArrow(k);
                        return (
                          <button key={k.key} onClick={() => setFcKey(k.key)} style={{
                            cursor: "pointer", fontFamily: MONO, fontSize: 10, borderRadius: 8, padding: "6px 10px",
                            color: sel ? C.text : C.muted, background: sel ? `${ac}22` : C.panel, border: `1px solid ${sel ? ac : C.border2}`,
                          }}>
                            {k.label}
                            <span style={{ marginLeft: 8, color: sel ? ac : C.muted }}>{fmtNum(k.forecast?.[0]?.y)}</span>
                            <span style={{ marginLeft: 6, color: arrow === "↑" ? C.green : arrow === "↓" ? C.amber : C.muted }}>{arrow}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}

                  <ChartWithSummary height={290} style={{ marginTop: 14 }} accent={ac} title={`GLR Forecast: ${fcKpi.label}`} subtitle={`AI model forecast of ${fcKpi.label} for the next ${recoveryForecast.horizon} cycles with ~80% confidence range`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={fcChart.data} margin={{ top: 8, right: 14, left: -4, bottom: 30 }}>
                        <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" type="number" domain={["dataMin", "dataMax"]} tickFormatter={v => `${v}`}
                          tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false}
                          label={{ value: "Cycle", position: "insideBottom", offset: -16, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
                        <YAxis domain={["auto", "auto"]} width={64} tickLine={false} axisLine={false}
                          tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickFormatter={v => fmtNum(v)}
                          label={{ value: `${fcKpi.label} (${fcKpi.unit})`, angle: -90, position: "insideLeft", offset: 12, fill: C.muted, fontSize: 9, fontFamily: MONO, style: { textAnchor: "middle" } }} />
                        <Tooltip isAnimationActive={false} wrapperStyle={{ zIndex: 99999 }} content={<ForecastTooltip kpi={fcKpi} kpis={fcKpis} />} />
                        <Area dataKey="bandBase" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" activeDot={false} />
                        <Area dataKey="bandSpread" stackId="band" stroke="none" fill={ac} fillOpacity={0.18} isAnimationActive={false} legendType="none" activeDot={false} />
                        <ReferenceLine x={fcChart.lastX} stroke={C.muted} strokeDasharray="4 3" label={{ value: "now", fill: C.muted, fontSize: 9, fontFamily: MONO, position: "top" }} />
                        <Line dataKey="avg" name="Average per cycle (actual)" stroke={C.green} strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={false} isAnimationActive={false} connectNulls />
                        <Line dataKey="pred_avg" name="Average per cycle (predicted)" stroke={C.amber} strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={false} isAnimationActive={false} connectNulls />
                        <Line dataKey="hist" name="Actual" stroke={C.cyan} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                        <Line dataKey="fc" name="Forecast" stroke={ac} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 4, fill: ac }} activeDot={{ r: 5 }} isAnimationActive={false} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartWithSummary>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
                    <span><span style={{ color: C.cyan }}>━</span> Actual</span>
                    <span><span style={{ color: C.green }}>╍╍</span> Average per cycle, actual (all cycles: {fmtNum(fcKpi.hist_avg)} {fcKpi.unit})</span>
                    <span><span style={{ color: ac }}>╍╍</span> Predicted</span>
                    <span><span style={{ color: C.amber }}>╍╍</span> Average per cycle, predicted ({fmtNum(fcKpi.pred_avg)} {fcKpi.unit})</span>
                    <span><span style={{ color: ac, opacity: 0.5 }}>▮</span> ~80% range</span>
                  </div>

                  <div style={{ overflowX: "auto", marginTop: 16, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: C.panel }}>
                          <th style={{ textAlign: "left", padding: "8px 10px", color: C.muted, fontWeight: 400, whiteSpace: "nowrap" }}>Cycle</th>
                          {fcKpis.map(k => (
                            <th key={k.key} style={{ textAlign: "right", padding: "8px 10px", color: k.key === fcKpi.key ? ac : C.muted, fontWeight: 400, whiteSpace: "nowrap" }}>
                              {k.label} <span style={{ opacity: 0.7 }}>({k.unit})</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(fcKpi.forecast || []).map((f, i) => {
                          const t = parseT(fcTimes[i]);
                          return (
                            <tr key={f.x} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ padding: "7px 10px", color: C.text, whiteSpace: "nowrap" }}>
                                #{f.x}<span style={{ color: C.muted, marginLeft: 8, fontSize: 9 }}>{t != null ? fmtDT(t) : ""}</span>
                              </td>
                              {fcKpis.map(k => {
                                const p = k.forecast?.[i];
                                return (
                                  <td key={k.key} title={p?.low != null ? `${fmtNum(p.low)} - ${fmtNum(p.high)} ${k.unit}` : undefined}
                                    style={{ textAlign: "right", padding: "7px 10px", color: k.key === fcKpi.key ? ac : C.text, whiteSpace: "nowrap" }}>
                                    {fmtNum(p?.y)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>
                    {recoveryForecast.method}
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: 16, maxHeight: 340, overflow: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
                <thead>
                  <tr>
                    {recHeaders.map((h, i) => (
                      <th key={h} style={{
                        padding: "8px 14px", textAlign: i === 0 || i === 1 ? "left" : "center",
                        color: C.teal, fontSize: 9, letterSpacing: 1,
                        borderBottom: `1px solid ${C.border}`, background: C.panel,
                        position: "sticky", top: 0, zIndex: 1,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recoveryRows.map((r, i) => {
                    const open = !!openRows[r.cycle];
                    const pp   = r.pain_points || [];
                    const dt   = r.details || {};
                    const kv = [
                      { k: "Shut-in time",          v: dt.shut_in_min,          u: "min" },
                      { k: "After-flow time",       v: dt.after_flow_min,       u: "min" },
                      { k: "Casing pressure (open)", v: dt.casing_open_psi,     u: "psi" },
                      { k: "Tubing pressure (open)", v: dt.tubing_open_psi,     u: "psi" },
                      { k: "Arrival velocity",      v: dt.arrival_velocity_ftmin, u: "ft/min" },
                      { k: "Load factor",           v: dt.load_factor,          u: "" },
                      { k: "Actual GLR",            v: dt.actual_glr,           u: "scf/bbl" },
                      { k: "Minimum GLR",           v: dt.min_glr,              u: "scf/bbl" },
                    ];
                    const lq = r.liquid_bbl, avgP = r._avg_pos, l10 = r._last10, e10 = r._exp10, ht = r.liquid_height_ft;
                    const statusText  = lq <= 0 ? "ZERO" : (avgP && lq < REC_WEAK_RATIO * avgP) ? "WEAK" : "GOOD";
                    const statusColor = lq <= 0 ? C.red : statusText === "WEAK" ? C.amber : C.green;
                    const notes = [
                      {
                        title: "LIQUID RECOVERED", val: lq.toFixed(3), unit: "barrels",
                        desc: "Liquid the plunger carried to surface in this cycle.",
                        ...(lq <= 0 ? { color: C.red, pain: "Nothing lifted: a lost cycle with zero production." }
                          : (avgP && lq < 0.5 * avgP) ? { color: C.amber, pain: `Weak lift: under half the usual ${avgP.toFixed(3)} barrels.` }
                          : { color: C.green, pain: "Healthy lift: in line with a normal recovering cycle." }),
                      },
                      {
                        title: "TOTAL RECOVERED SO FAR", val: r.cumulative_bbl.toFixed(3), unit: "barrels",
                        desc: "Running total of liquid brought up from cycle 1 to this cycle.",
                        ...(l10 != null && l10 <= 0 ? { color: C.red, pain: "Flat curve: no growth in the last 10 cycles; the well is not recovering." }
                          : (l10 != null && e10 && l10 < 0.5 * e10) ? { color: C.amber, pain: `Slow growth: only ${l10.toFixed(3)} barrels added in 10 cycles (usual ${e10.toFixed(3)}).` }
                          : { color: C.green, pain: "Steady growth: the total is rising as expected." }),
                      },
                      {
                        title: "LIQUID COLUMN HEIGHT", val: ht != null ? ht.toFixed(1) : "-", unit: "feet",
                        desc: "Height of liquid built up in the tubing before this cycle opened.",
                        ...(ht == null ? { color: C.muted, pain: "No height data for this cycle." }
                          : ht > 0 && lq <= 0 ? { color: C.red, pain: "Loaded but not lifted: the liquid column stayed behind." }
                          : ht > 0 ? { color: C.green, pain: "Column was lifted: recovery matches the liquid load." }
                          : { color: C.muted, pain: "No liquid column built up: nothing to lift." }),
                      },
                    ];
                    return (
                      <Fragment key={r.cycle}>
                        <tr onClick={() => setOpenRows(o => ({ ...o, [r.cycle]: !o[r.cycle] }))}
                          style={{ background: i % 2 === 0 ? C.card : C.panel, cursor: "pointer" }}>
                          <td style={{ padding: "7px 14px", fontWeight: 700, color: C.amber, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>
                            <svg width="7" height="7" viewBox="0 0 10 10" fill={C.text}
                              style={{ marginRight: 9, verticalAlign: "middle", transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "none" }}>
                              <path d="M2 0.5L9 5L2 9.5Z" />
                            </svg>
                            {r.cycle}
                          </td>
                          <td style={{ padding: "7px 14px", color: C.muted, borderBottom: `1px solid ${C.border}` }}>{r._t != null ? fmtDT(r._t) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: r.liquid_bbl > 0 ? C.cyan : C.muted, borderBottom: `1px solid ${C.border}` }}>{r.liquid_bbl.toFixed(3)}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: C.purple, borderBottom: `1px solid ${C.border}` }}>{r.cumulative_bbl.toFixed(3)}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: r.liquid_height_ft > 0 ? C.teal : C.muted, borderBottom: `1px solid ${C.border}` }}>{r.liquid_height_ft != null ? r.liquid_height_ft.toFixed(1) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: C.green, borderBottom: `1px solid ${C.border}` }}>{r.avg_bbl_to_date != null ? r.avg_bbl_to_date.toFixed(3) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: C.amber, borderBottom: `1px solid ${C.border}` }}>{r.rolling_avg_bbl != null ? r.rolling_avg_bbl.toFixed(3) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: r.vs_avg_pct == null ? C.muted : r.vs_avg_pct >= 0 ? C.green : C.amber, borderBottom: `1px solid ${C.border}` }}>
                            {r.vs_avg_pct != null ? `${r.vs_avg_pct > 0 ? "+" : ""}${r.vs_avg_pct.toFixed(1)}%` : "-"}
                          </td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: C.text, borderBottom: `1px solid ${C.border}` }}>{dt.shut_in_min != null ? Number(dt.shut_in_min).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: C.text, borderBottom: `1px solid ${C.border}` }}>{dt.actual_glr != null ? Number(dt.actual_glr).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "-"}</td>
                          <td style={{ padding: "7px 14px", textAlign: "center", color: statusColor, fontWeight: 700, letterSpacing: 1, fontSize: 9, borderBottom: `1px solid ${C.border}` }}>{statusText}</td>
                        </tr>
                        {open && (
                          <tr style={{ background: C.panel }}>
                            <td colSpan={recHeaders.length} style={{ padding: "12px 18px 14px", borderBottom: `1px solid ${C.border}` }}>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 14 }}>
                                {notes.map(n => (
                                  <div key={n.title} style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${n.color}`, borderRadius: 8, padding: "9px 12px" }}>
                                    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: C.muted }}>{n.title}</div>
                                    <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: n.color, margin: "3px 0 5px" }}>
                                      {n.val}<span style={{ color: C.muted, fontSize: 9, fontWeight: 400, marginLeft: 4 }}>{n.unit}</span>
                                    </div>
                                    <div style={{ fontFamily: MONO, fontSize: 10, lineHeight: 1.5, color: C.muted }}>{n.desc}</div>
                                    <div style={{ fontFamily: MONO, fontSize: 10, lineHeight: 1.5, color: n.color, marginTop: 5 }}>{n.pain}</div>
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                                <div style={{ flex: "1 1 320px" }}>
                                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: C.muted, marginBottom: 8 }}>KEY VALUES</div>
                                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                                    {kv.map(x => (
                                      <div key={x.k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px" }}>
                                        <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>{x.k}</div>
                                        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text, marginTop: 2 }}>
                                          {x.v != null ? Number(x.v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-"}
                                          {x.v != null && x.u && <span style={{ color: C.muted, fontSize: 9, fontWeight: 400, marginLeft: 4 }}>{x.u}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div style={{ flex: "1 1 320px" }}>
                                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: C.muted, marginBottom: 8 }}>OPERATIONAL ISSUES</div>
                                  {pp.length === 0 ? (
                                    <div style={{ fontFamily: MONO, fontSize: 11, color: C.green, background: `${C.green}12`, border: `1px solid ${C.green}33`, borderRadius: 6, padding: "6px 10px" }}>
                                      No issues found in this cycle.
                                    </div>
                                  ) : pp.map((x, k) => {
                                    const col = x.level === "critical" ? C.red : C.amber;
                                    return (
                                      <div key={k} style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1.5, color: col, background: `${col}12`, border: `1px solid ${col}33`, borderRadius: 6, padding: "6px 10px", marginBottom: 6 }}>
                                        <b style={{ letterSpacing: 1, fontSize: 9, marginRight: 6 }}>{x.level === "critical" ? "CRITICAL" : "WARNING"}</b>{x.text}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </FadeCard>
  );
}

/* MAIN PAGE */
function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/>
        <line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/>
      </svg>
    ),
    "■": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/>
        <path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    "◆": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/>
        <path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▽": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▶": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.5}}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

export default function GLRFeasibilityPage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  const { apiUrl } = useWell();
  const [glrData,   setGlrData]   = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [wellSpecs, setWellSpecs] = useState({});
  const [loading,   setLoading]   = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [glrRes, specsRes] = await Promise.all([
        axios.get(apiUrl("/glr-feasibility")),
        axios.get(apiUrl("/well-specs")),
      ]);
      const glr = glrRes.data;
      if (Array.isArray(glr?.per_cycle_data)) {
        glr.per_cycle_data = glr.per_cycle_data.map(d => ({ ...d, _t: parseT(d.time) }));   // _t = open time in ms (timestamp axis)
      }
      setGlrData(glr);
      setTrendData(glr?.per_cycle_data || glr?.trend_data || []);
      setWellSpecs(specsRes.data || {});
    } catch (err) {
      console.warn("GLR endpoint not available yet:", err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ───────────── TIME WINDOW STATE ───────────── */
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });

  const stamped = trendData;   // every row already carries _t (ms) from fetchData
  const tMin = useMemo(() => { const v = stamped.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [stamped]);
  const tMax = useMemo(() => { const v = stamped.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [stamped]);

  // presets are measured back from the LATEST cycle in the data, not from "now"
  const rowsFor = (w) => {
    if (w.mode === "all" || tMax == null) return stamped;
    if (w.mode === "cyc30") return stamped.slice(-30);
    if (w.mode === "custom") {
      const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
      const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
      return stamped.filter(d => d._t != null && d._t >= f && d._t <= t);
    }
    const p = PRESETS.find(x => x.id === w.mode);
    return stamped.filter(d => d._t != null && d._t >= tMax - p.ms);
  };
  const view = useMemo(() => rowsFor(win), [stamped, win, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, rowsFor({ mode: p.id }).length])), [stamped, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const isAll = win.mode === "all";

  // "ALL" keeps the backend numbers exactly as before; any other window recomputes stats from the cycles inside it
  const viewGlr = useMemo(() => {
    if (isAll || !glrData) return glrData;
    return { ...glrData, per_cycle_data: view, ...glrStatsOf(view) };
  }, [isAll, glrData, view]);

  return (
    <div style={{ animation: "fade-up 0.4s ease both" }}>

      {/* Calculator */}
      <div style={{ marginBottom: 18 }}>
        <Calculator wellSpecs={wellSpecs} />
      </div>

      {/* Data window (timestamps / presets) */}
      {!loading && trendData.length > 0 && (
        <DataWindowBar win={win} setWin={setWin} stamped={stamped} view={view}
          presetCounts={presetCounts} tMin={tMin} tMax={tMax} isDark={isDark} />
      )}

      {/* Per-cycle distribution */}
      {(isAll || view.length > 0) && (
        <div style={{ marginBottom: 18 }}>
          <DatasetGLRPanel glrData={viewGlr} loading={loading} />
        </div>
      )}

      {/* Trend line chart */}
      {(isAll ? trendData : view).length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <GLRTrendPanel trendData={isAll ? trendData : view} loading={loading} />
        </div>
      )}

      {/* Reference table */}
      <DepthTable />
    </div>
  );
}